import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { StudentLayout } from "@/components/Layouts";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { StatusBadge } from "@/components/StatusBadge";
import { RecordingCard } from "@/components/RecordingCard";
import { TradingChartLoader } from "@/components/TradingChartLoader";
import { EmptyState } from "@/components/EmptyState";
import { ArrowLeft, ArrowRight, CheckCircle2, Circle, Layers } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Markdown } from "@/components/Markdown";

export default function StudentModuleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [mod, setMod] = useState(null);
  const [allModules, setAllModules] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [m, list] = await Promise.all([
        api.get(`/modules/${id}`).then((r) => r.data),
        api.get("/modules").then((r) => r.data),
      ]);
      setMod(m);
      setAllModules(list);
    } catch {
      toast.error("Module not found.");
      navigate("/student/modules");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const handleToggle = async () => {
    try {
      if (mod.completed) await api.post(`/modules/${id}/uncomplete`);
      else await api.post(`/modules/${id}/complete`);
      await load();
      await refreshUser();
    } catch {
      toast.error("Gagal kemas kini.");
    }
  };

  if (loading || !mod) {
    return <StudentLayout title="Module"><div className="flex items-center justify-center py-16"><TradingChartLoader size="lg" text="Loading module..." /></div></StudentLayout>;
  }

  const idx = allModules.findIndex((x) => x.id === id);
  const next = idx >= 0 && idx < allModules.length - 1 ? allModules[idx + 1] : null;
  const parts = mod.parts || [];

  return (
    <StudentLayout title={mod.title}>
      <Button asChild variant="ghost" size="sm" className="mb-4" data-testid="back-to-course">
        <Link to="/student/course"><ArrowLeft className="h-4 w-4" /> Back</Link>
      </Button>

      <div className="space-y-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <span className="text-xs font-mono font-semibold text-primary tracking-wider">{mod.code}</span>
            <h1 className="text-3xl font-heading font-bold tracking-tight mt-1">{mod.title}</h1>
            {mod.description && (
              <div className="mt-2 max-w-3xl">
                <Markdown>{mod.description}</Markdown>
              </div>
            )}
          </div>
          <StatusBadge status={mod.status} />
        </div>

        {mod.status !== "tersedia" ? (
          <Card className="glass-card">
            <CardContent className="p-8 text-center">
              <h2 className="text-lg font-heading font-semibold">Module Not Available</h2>
              <p className="mt-2 text-muted-foreground">
                This module's content is not yet available. Please check again later.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {mod.content && (
              <Card className="glass-card">
                <CardContent className="p-6 sm:p-8">
                  <Markdown>{mod.content}</Markdown>
                </CardContent>
              </Card>
            )}

            <section>
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <h2 className="text-lg font-heading font-semibold flex items-center gap-2">
                  <Layers className="h-4 w-4 text-primary" /> Video Parts
                </h2>
                {parts.length > 0 && (
                  <span className="text-xs text-muted-foreground">{parts.length} {parts.length === 1 ? "part" : "parts"}</span>
                )}
              </div>

              {parts.length === 0 ? (
                <EmptyState
                  icon={Layers}
                  title="Video parts are not yet available."
                  description="Video parts will be added by admin shortly."
                />
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {parts.map((p) => (
                    <RecordingCard
                      key={p.id}
                      recording={p}
                      playbackPath={`/modules/${id}/parts/${p.id}/playback`}
                      refreshPath={`/modules/${id}/parts/${p.id}/playback/refresh`}
                    />
                  ))}
                </div>
              )}
            </section>

            <div className="flex items-center justify-between flex-wrap gap-3 pt-4 border-t border-border">
              <Button onClick={handleToggle} variant={mod.completed ? "secondary" : "default"} data-testid="module-complete-toggle">
                {mod.completed ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-4 w-4" />}
                {mod.completed ? "Mark as Not Completed" : "Mark as Completed"}
              </Button>
              {next && (
                <Button asChild variant="outline" data-testid="next-module-btn">
                  <Link to={`/student/module/${next.id}`}>
                    Next Module: {next.code} <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </StudentLayout>
  );
}
